import { useState } from 'react'
import { NoPhoto } from './NoPhoto.js'
import { t } from '../lib/i18n.js'

/**
 * The venue thumbnail - build-spec §7b.
 *
 * Proxied through our API, never a Google URL: the frontend does not talk to
 * Google (spec §8), the key stays server-side, and Google's own photo URLs
 * expire, which would leave a broken image on a cached page.
 *
 * Always renders something. Collapsing when there is no photo made cards
 * different heights, which in a slider means the layout jumps every time you
 * swipe past one - worse than a plain placeholder that says so.
 */

interface Props {
  token: string
  /**
   * The venue this photo belongs to. Part of the URL, not decoration: the
   * endpoint always serves "the current candidate", so without it every
   * suggestion shares one URL and the browser happily reuses the cached
   * image - which showed the first venue's photo on every later card.
   */
  placeId: string
  /** From the venue payload; false means Google had no photo for this place. */
  hasPhoto: boolean
  name: string
  /** Shorter image when three cards share a screen. */
  compact?: boolean | undefined
}

export function VenuePhoto({ token, placeId, hasPhoto, name, compact }: Props) {
  // A photo can also fail after we said it exists - the reference can expire
  // between the pipeline run and the view.
  const [failed, setFailed] = useState(false)

  const className = compact ? 'venue-photo venue-photo-compact' : 'venue-photo'

  if (!hasPhoto || failed) {
    return (
      <div className={`${className} venue-photo-empty`}>
        <NoPhoto className="no-photo-art" />
        <span>{t('venue.noPhoto')}</span>
      </div>
    )
  }

  return (
    <img
      // `place` identifies which venue's photo this is, so each suggestion
      // gets its own cache entry. The server verifies it against the current
      // candidate rather than trusting it.
      src={`/api/meetings/${token}/photo?w=640&place=${encodeURIComponent(placeId)}`}
      // Remount on venue change, so a stale image never lingers while the new
      // one loads.
      key={placeId}
      className={className}
      alt={name}
      onError={() => setFailed(true)}
      loading="lazy"
      decoding="async"
    />
  )
}
